import { FC } from 'react'
import { alpha } from '@mui/material/styles'
import { Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import R4RIcon from 'components/R4RIcon'
import {
  WidgetCard,
  WidgetCardHeader,
  WidgetCardBody,
  WidgetBadge,
  WidgetOutlineButton
} from './WidgetCard'
import { useBenefitsList } from 'services/benefits/benefits.hooks'

const DESKTOP_TILES_COUNT = 8

const WidgetDiscount: FC = () => {
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { benefitsList = [], isFetching, isLoading } = useBenefitsList()

  const tileGradients = [
    'linear-gradient(135deg, #FEC900 0%, #24D4E1 100%)',
    'linear-gradient(135deg, #7F71D3 0%, #6A8AE5 100%)',
    'linear-gradient(135deg, #6A8AE5 0%, #24D4E1 100%)',
    theme.r4r.gradient
  ]

  // Flat 65% overlays from the Figma "Rabaty" tiles.
  const tileOverlays = [
    alpha(theme.r4r.navy, 0.65),
    alpha(theme.r4r.azure, 0.65),
    alpha(theme.r4r.violet, 0.65)
  ]

  if (isFetching || isLoading) {
    return (
      <WidgetCard
        sx={{ flex: 1, minWidth: 0, padding: '24px', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </WidgetCard>
    )
  }

  if (benefitsList.length < 1) {
    return null
  }

  const activeCountBadge = (
    <WidgetBadge background={alpha(theme.r4r.cyan, 0.2)} color={theme.r4r.cyan}>
      {formatMessage(
        { id: 'discountWidget.activeCount', defaultMessage: '{count} aktywne' },
        { count: benefitsList.length }
      )}
    </WidgetBadge>
  )

  if (isDesktop) {
    return (
      <WidgetCard sx={{ width: '100%' }}>
        <WidgetCardHeader
          icon='pricetag'
          title={formatMessage({ id: 'dashboard.discounts', defaultMessage: 'Rabaty' })}
        />
        <WidgetCardBody sx={{ gap: '14px' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {benefitsList.slice(0, DESKTOP_TILES_COUNT).map((benefit, index) => (
              <Box
                key={benefit.id}
                sx={{
                  width: '110px',
                  height: '72px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: tileOverlays[index % tileOverlays.length],
                  overflow: 'hidden',
                  px: '6px'
                }}>
                <Typography
                  noWrap
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '10px',
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                    textTransform: 'uppercase',
                    textShadow: '0px 1px 3px rgba(0, 0, 0, 0.5)',
                    maxWidth: '100px',
                    mb: '2px'
                  }}>
                  {benefit.benefitCategory?.namePl || ''}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    color: '#fff',
                    fontSize: '18px',
                    fontWeight: 800,
                    textShadow: '0px 1px 4px rgba(0, 0, 0, 0.5)',
                    maxWidth: '100px'
                  }}>
                  {benefit.name}
                </Typography>
              </Box>
            ))}
          </Box>
          <WidgetOutlineButton onClick={() => navigate('/discounts')}>
            {formatMessage({
              id: 'discountWidget.seeAllDiscounts',
              defaultMessage: 'Zobacz wszystkie rabaty'
            })}
          </WidgetOutlineButton>
        </WidgetCardBody>
      </WidgetCard>
    )
  }

  return (
    <WidgetCard sx={{ padding: '14px 18px 24px', gap: '10px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.r4r.navy }}>
          <R4RIcon name='pricetag' size={16} />
          <Typography sx={{ fontSize: '15px', fontWeight: 700, color: theme.r4r.navy }}>
            {formatMessage({ id: 'dashboard.discounts', defaultMessage: 'Rabaty' })}
          </Typography>
        </Box>
        {activeCountBadge}
      </Box>
      <Box sx={{ display: 'flex', gap: '10px', overflowX: 'auto', pb: '2px' }}>
        {benefitsList.slice(0, 4).map((benefit, index) => (
          <Box
            key={benefit.id}
            sx={{
              width: '110px',
              height: '72px',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: tileGradients[index % tileGradients.length],
              overflow: 'hidden',
              px: '6px'
            }}>
            <Typography
              noWrap
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.3px',
                textTransform: 'uppercase',
                textShadow: '0px 1px 3px rgba(0, 0, 0, 0.5)',
                maxWidth: '100px'
              }}>
              {benefit.benefitCategory?.namePl || ''}
            </Typography>
            <Typography
              noWrap
              sx={{
                color: '#fff',
                fontSize: '14px',
                fontWeight: 800,
                textShadow: '0px 1px 4px rgba(0, 0, 0, 0.5)',
                maxWidth: '100px'
              }}>
              {benefit.name}
            </Typography>
          </Box>
        ))}
      </Box>
      <Button
        fullWidth
        variant='outlined'
        onClick={() => navigate('/discounts')}
        endIcon={<R4RIcon name='arrowForward' size={18} />}
        sx={{
          borderColor: theme.r4r.navy,
          color: theme.r4r.navy,
          fontSize: '13px',
          fontWeight: 500,
          minHeight: '28px',
          padding: '5px 13px',
          borderRadius: 0,
          '&:hover': { borderColor: theme.r4r.navy }
        }}>
        {formatMessage({
          id: 'discountWidget.seeAllDiscounts',
          defaultMessage: 'Zobacz wszystkie rabaty'
        })}
      </Button>
    </WidgetCard>
  )
}

export default WidgetDiscount
